import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const STATUS_COLORS = {
  draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
  planned: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  ready: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800',
  completed: 'bg-gray-100 text-gray-600 dark:bg-gray-700/30 dark:text-gray-400 border-gray-200 dark:border-gray-700',
  archived: 'bg-gray-100 text-gray-500 dark:bg-gray-700/30 dark:text-gray-500 border-gray-200 dark:border-gray-700',
};

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

function formatTime(isoString) {
  const d = new Date(isoString);
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'p' : 'a';
  const hour = h % 12 || 12;
  return m === 0 ? `${hour}${ampm}` : `${hour}:${m.toString().padStart(2, '0')}${ampm}`;
}

function isSameDay(date1, date2) {
  return date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate();
}

export default function MonthView({ year, month, events, courses, onEventClick, onDayClick, onNavigate }) {
  const { t } = useTranslation();
  const today = new Date();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const monthName = new Date(year, month).toLocaleString('default', { month: 'long' });

  // Build day cells including padding from prev/next month
  const prevMonthDays = getDaysInMonth(year, month - 1);
  const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;

  const cells = [];
  for (let i = 0; i < totalCells; i++) {
    const dayOffset = i - firstDay;
    if (dayOffset < 0) {
      // Previous month
      cells.push({ day: prevMonthDays + dayOffset + 1, inMonth: false, date: new Date(year, month - 1, prevMonthDays + dayOffset + 1) });
    } else if (dayOffset >= daysInMonth) {
      // Next month
      cells.push({ day: dayOffset - daysInMonth + 1, inMonth: false, date: new Date(year, month + 1, dayOffset - daysInMonth + 1) });
    } else {
      cells.push({ day: dayOffset + 1, inMonth: true, date: new Date(year, month, dayOffset + 1) });
    }
  }

  // Map events to days
  function getEventsForDay(date) {
    return events.filter(e => {
      const start = new Date(e.start);
      return isSameDay(start, date);
    });
  }

  // Check if a course spans a given date
  function getCoursesForDay(date) {
    return courses.filter(c => {
      const start = new Date(c.starts_on);
      const end = c.ends_on ? new Date(c.ends_on) : null;
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      if (end) {
        const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
        return d >= s && d <= e;
      }
      return isSameDay(d, s);
    });
  }

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => onNavigate(month === 0 ? year - 1 : year, month === 0 ? 11 : month - 1)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
          aria-label={t('teacherCalendar.previousMonth')}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {monthName} {year}
        </h3>
        <button
          onClick={() => onNavigate(month === 11 ? year + 1 : year, month === 11 ? 0 : month + 1)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
          aria-label={t('teacherCalendar.nextMonth')}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAYS.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {weeks.map((week, wi) => (
          <div key={wi} className={cn('grid grid-cols-7', wi > 0 && 'border-t border-gray-200 dark:border-gray-700')}>
            {week.map((cell, ci) => {
              const isToday = isSameDay(cell.date, today);
              const dayEvents = getEventsForDay(cell.date);
              const dayCoursesActive = getCoursesForDay(cell.date);
              const maxVisible = 3;

              return (
                <div
                  key={ci}
                  className={cn(
                    'min-h-[100px] p-1.5 border-r border-gray-200 dark:border-gray-700 last:border-r-0 transition-colors',
                    cell.inMonth ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-850 dark:bg-gray-900/50',
                    'hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer'
                  )}
                  onClick={() => onDayClick && onDayClick(cell.date)}
                >
                  {/* Day number */}
                  <div className="flex items-center justify-between mb-1">
                    <span className={cn(
                      'text-sm leading-6 inline-flex items-center justify-center',
                      isToday && 'bg-blue-600 text-white rounded-full w-6 h-6 font-semibold',
                      !isToday && cell.inMonth && 'text-gray-900 dark:text-gray-100',
                      !isToday && !cell.inMonth && 'text-gray-400 dark:text-gray-600'
                    )}>
                      {cell.day}
                    </span>
                    {dayCoursesActive.length > 0 && (
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400" title={dayCoursesActive.map(c => c.title).join(', ')} />
                    )}
                  </div>

                  {/* Events */}
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, maxVisible).map(event => (
                      <button
                        key={event.id}
                        onClick={(e) => { e.stopPropagation(); onEventClick && onEventClick(event); }}
                        className={cn(
                          'w-full text-left text-[11px] leading-tight px-1.5 py-0.5 rounded border truncate block',
                          STATUS_COLORS[event.status] || STATUS_COLORS.active
                        )}
                        title={`${event.title} (${formatTime(event.start)})`}
                      >
                        <span className="font-medium">{formatTime(event.start)}</span>{' '}
                        {event.title}
                      </button>
                    ))}
                    {dayEvents.length > maxVisible && (
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 pl-1.5">
                        +{dayEvents.length - maxVisible} {t('common.more')}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
